import {
  activateProgramDefinition,
  activateProtocolDefinition,
  activateTreatmentDefinition,
  deactivateProgramDefinition,
  deactivateProtocolDefinition,
  deactivateTreatmentDefinition,
  type PublicationStatus,
  useDiagnosticDefinitions,
  useProgramDefinitions,
  useProtocolDefinitionsResult,
  useTreatmentDefinitions,
} from "@repo/data-store";
import { MarkdownContent } from "@repo/ui/components/markdown-content";
import { createFileRoute } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { CatalogItemLink, type ProcedureCatalogItemType } from "@/features/catalog";
import { ProtocolProjectionWarning } from "@/features/catalog/components/views/protocol-projection-warning";
import { CATALOG_STATUS_OPTIONS } from "@/features/catalog/lib/catalog-status-options";
import { diagnosticDefinitionRouteId } from "@/features/catalog/lib/diagnostic-definition-route";
import {
  programDefinitionRouteId,
  programDefinitionUrlFromRouteId,
} from "@/features/catalog/lib/program-definition-route";
import {
  treatmentDefinitionRouteId,
  treatmentDefinitionUrlFromRouteId,
} from "@/features/catalog/lib/treatment-definition-route";
import { ActionButton } from "@/shared/common/action-button";
import { GroupedList, type GroupedListGroup } from "@/shared/common/grouped-list";
import { SearchFilterBar } from "@/shared/common/search-filter-bar";
import { formatDate } from "@/shared/lib/date-formats";
import { StatusPickerBadge } from "@/shared/status/status-picker-badge";

export const Route = createFileRoute("/catalog/_layout/procedures")({
  component: CatalogComponent,
  validateSearch: (search: Record<string, unknown>): { q?: string; status?: string; group?: string } => ({
    q: (search.q as string) || undefined,
    status: (search.status as string) || undefined,
    group: (search.group as string) || undefined,
  }),
});

/**
 * Four kinds share this tab, so this is the one list that needs a common row.
 * It is declared here, next to the mapping, rather than shared: a row type any
 * kind could satisfy is how one component ends up impersonating five.
 */
interface ProcedureRow {
  id: string;
  kind: ProcedureCatalogItemType;
  title: string;
  description?: string;
  updatedAt?: string;
  status: PublicationStatus;
}

/** Diagnostics are revised, never flipped from a list. */
function canChangeStatus(row: ProcedureRow): boolean {
  return row.kind === "protocol" || (row.kind !== "diagnostic" && row.status !== "retired");
}

function CatalogComponent() {
  const { q: search = "", status: statusFilter, group: focusGroup } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { permissions } = Route.useRouteContext();
  const setSearch = (value: string) =>
    navigate({ search: (prev) => ({ ...prev, q: value || undefined }), replace: true });
  const setStatusFilter = (value: string | null) =>
    navigate({ search: (prev) => ({ ...prev, status: value || undefined }), replace: true });

  const canCreateActivityDefinition = permissions.canWrite("ActivityDefinition");
  const canCreatePlanDefinition = permissions.canWrite("PlanDefinition");

  const { definitions: allProtocolDefinitions, failures: protocolFailures } = useProtocolDefinitionsResult({
    search,
    ...(statusFilter === "active"
      ? { availability: "Active" as const }
      : statusFilter === "retired"
        ? { availability: "Inactive" as const }
        : {}),
  });
  const protocolDefinitions = statusFilter === "draft" ? [] : allProtocolDefinitions;
  const programDefinitions = useProgramDefinitions({
    search,
    ...(statusFilter === "active"
      ? { availability: "Active" as const }
      : statusFilter === "retired"
        ? { availability: "Inactive" as const }
        : {}),
  });
  const allDiagnosticDefinitions = useDiagnosticDefinitions({
    search,
    ...(statusFilter === "active"
      ? { availability: "Active" as const }
      : statusFilter === "retired"
        ? { availability: "Inactive" as const }
        : {}),
  });
  const diagnosticDefinitions = statusFilter === "draft" ? [] : allDiagnosticDefinitions;
  const allTreatmentDefinitions = useTreatmentDefinitions({
    search,
    ...(statusFilter === "active"
      ? { availability: "Active" as const }
      : statusFilter === "retired"
        ? { availability: "Inactive" as const }
        : {}),
  });
  const treatmentDefinitions = statusFilter === "draft" ? [] : allTreatmentDefinitions;

  const isLoading = false;

  const handleStatusChange = (row: ProcedureRow, status: string) => {
    if (!canChangeStatus(row)) return;
    if (status !== "active" && status !== "retired") return;

    if (row.kind === "protocol") {
      void (
        status === "active"
          ? activateProtocolDefinition({ definitionUrl: row.id })
          : deactivateProtocolDefinition({ definitionUrl: row.id })
      ).isPersisted.promise.catch(() => {});
      return;
    }
    if (row.kind === "treatment") {
      const url = treatmentDefinitionUrlFromRouteId(row.id);
      if (!url) return;
      void (
        status === "active"
          ? activateTreatmentDefinition({ definitionUrl: url })
          : deactivateTreatmentDefinition({ definitionUrl: url })
      ).isPersisted.promise.catch(() => {});
      return;
    }

    void (
      status === "active"
        ? activateProgramDefinition({ definitionUrl: programDefinitionUrlFromRouteId(row.id) ?? row.id })
        : deactivateProgramDefinition({ definitionUrl: programDefinitionUrlFromRouteId(row.id) ?? row.id })
    ).isPersisted.promise.catch(() => {});
  };

  const sections: Array<{
    id: string;
    name: string;
    kind: ProcedureCatalogItemType;
    rows: ProcedureRow[];
    canCreate: boolean;
  }> = [
    {
      id: "programs",
      name: "Programs",
      kind: "program",
      rows: programDefinitions.map((d) => ({
        id: programDefinitionRouteId(d.definitionUrl),
        kind: "program",
        title: d.name,
        ...(d.description ? { description: d.description } : {}),
        updatedAt: d.mintedAt,
        status: d.availability === "Active" ? "active" : "retired",
      })),
      canCreate: canCreatePlanDefinition,
    },
    {
      id: "protocols",
      name: "Protocols",
      kind: "protocol",
      rows: protocolDefinitions.map((definition) => ({
        id: definition.definitionUrl,
        kind: "protocol",
        title: definition.name,
        ...(definition.description ? { description: definition.description } : {}),
        updatedAt: definition.mintedAt,
        status: definition.availability === "Active" ? "active" : "retired",
      })),
      canCreate: canCreatePlanDefinition,
    },
    {
      id: "diagnostics",
      name: "Diagnostics",
      kind: "diagnostic",
      rows: diagnosticDefinitions.map((definition) => ({
        id: diagnosticDefinitionRouteId(definition.definitionUrl),
        kind: "diagnostic",
        title: definition.name,
        ...(definition.description ? { description: definition.description } : {}),
        updatedAt: definition.mintedAt,
        status: definition.availability === "Active" ? "active" : "retired",
      })),
      canCreate: canCreateActivityDefinition,
    },
    {
      id: "treatments",
      name: "Treatments",
      kind: "treatment",
      rows: treatmentDefinitions.map((definition) => ({
        id: treatmentDefinitionRouteId(definition.url),
        kind: "treatment",
        title: definition.name,
        ...(definition.description ? { description: definition.description } : {}),
        updatedAt: definition.mintedAt,
        status: definition.availability === "Active" ? "active" : "retired",
      })),
      canCreate: canCreateActivityDefinition,
    },
  ];

  const groups: GroupedListGroup<ProcedureRow>[] = sections
    .filter((s) => s.rows.length > 0 || s.canCreate)
    .map((s) => ({
      id: s.id,
      name: s.name,
      items: s.rows,
      ...(s.canCreate
        ? {
            headerAction: () => {
              const props = { state: { allowed: true } as const, variant: "ghost" as const, size: "icon-xs" as const };
              if (s.kind === "protocol") {
                return (
                  <ActionButton {...props} to="/catalog/protocols/new">
                    <Plus className="size-4" />
                  </ActionButton>
                );
              }
              if (s.kind === "diagnostic") {
                return (
                  <ActionButton {...props} to="/catalog/diagnostics/new">
                    <Plus className="size-4" />
                  </ActionButton>
                );
              }
              if (s.kind === "treatment") {
                return (
                  <ActionButton {...props} to="/catalog/treatments/new">
                    <Plus className="size-4" />
                  </ActionButton>
                );
              }
              return (
                <ActionButton {...props} to="/catalog/procedures/new">
                  <Plus className="size-4" />
                </ActionButton>
              );
            },
          }
        : {}),
    }));

  const statusCell = (row: ProcedureRow) => (
    <StatusPickerBadge
      value={row.status}
      options={CATALOG_STATUS_OPTIONS}
      onChange={(status) => handleStatusChange(row, status)}
      disabled={!canChangeStatus(row)}
    />
  );

  return (
    <GroupedList
      focusGroup={focusGroup}
      groups={groups}
      isLoading={isLoading}
      header={
        <>
          {statusFilter !== "draft" && <ProtocolProjectionWarning failures={protocolFailures} />}
          <SearchFilterBar
            search={search}
            onSearchChange={setSearch}
            placeholder="Search procedures..."
            statusOptions={[
              { value: "active", label: "Active" },
              { value: "draft", label: "Draft" },
              { value: "retired", label: "Retired" },
            ]}
            statusValue={statusFilter ?? null}
            onStatusChange={setStatusFilter}
          />
        </>
      }
      columns={[
        { id: "title", width: "1fr" },
        { id: "date", width: "auto" },
        { id: "status", width: "100px" },
      ]}
      columnGap="4px"
      renderCells={(row) => [
        <div key="title" className="flex flex-col gap-0.5 min-w-0">
          <span className="font-medium text-sm">{row.title}</span>
          {row.description && (
            <MarkdownContent className="text-xs text-muted-foreground line-clamp-1">{row.description}</MarkdownContent>
          )}
        </div>,
        row.updatedAt ? (
          <span key="date" className="text-xs text-muted-foreground text-right">
            {formatDate(row.updatedAt)}
          </span>
        ) : null,
        /* biome-ignore lint/a11y/noStaticElementInteractions: stop the picker from navigating the parent link */
        <div
          key="status"
          className="flex justify-end"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {statusCell(row)}
        </div>,
      ]}
      wrapItem={(row, children) => (
        <CatalogItemLink id={row.id} type={row.kind} className="cursor-pointer">
          {children}
        </CatalogItemLink>
      )}
      renderItem={(row) => (
        <CatalogItemLink
          id={row.id}
          type={row.kind}
          className="flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors cursor-pointer"
        >
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="font-medium text-sm">{row.title}</span>
            {row.description && (
              <MarkdownContent className="text-xs text-muted-foreground line-clamp-1">
                {row.description}
              </MarkdownContent>
            )}
          </div>
          {/* biome-ignore lint/a11y/noStaticElementInteractions: stop the picker from navigating the parent link */}
          <div
            className="flex items-center gap-4 shrink-0"
            onClick={(e) => e.preventDefault()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {row.updatedAt && <span className="text-xs text-muted-foreground">{formatDate(row.updatedAt)}</span>}
            {statusCell(row)}
          </div>
        </CatalogItemLink>
      )}
      getItemKey={(row) => row.id}
      emptyMessage="No items found"
      isFiltered={!!search || !!statusFilter}
    />
  );
}
